// Purchase orders — data access (docs/design/procurement.md, ADR-020).
//
//   list     rpc purchase_order_page(company, status, term, after_date, after_number, limit 51)
//            keyset on (order_date desc, number desc); indexes purchase_orders_open_idx /
//            _status_idx / _list_idx; search: number prefix (purchase_orders_number_key)
//            or supplier trigram. One request per page, ≈ 200 B per row.
//   detail   rpc purchase_order_document(id) — header + lines with codes, one round-trip
//            (60 lines ≈ 42 KB, ~7 ms server).
//   writes   rpc save_purchase_order / release_ / cancel_ / close_purchase_order_line /
//            close_purchase_order. Tables are read-only for API users; the database
//            validates everything and writes the audit log. save sends expected_revision,
//            so a stale editor cannot overwrite a colleague's change.
// Money and quantities travel as decimal strings (Postgres numeric), never floats.

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import type { Page } from "@/components/DataList";
import { isDemoMode, supabase } from "@/lib/supabase";
import { demoPoApi } from "./demo";
import { sortParam, type ListFilter, type ListSort } from "@patronix/domain/list";
import type { SendMaterialInput, SendPick } from "@/components/SendMaterialForm";

export const purchaseOrderStatuses = ["draft", "released", "partially_received", "received", "closed", "cancelled"] as const;
export type PurchaseOrderStatus = (typeof purchaseOrderStatuses)[number];

export type StatusFilter = "open" | "all" | PurchaseOrderStatus;

export function isStatusFilter(v: unknown): v is StatusFilter {
  return v === "open" || v === "all" || (purchaseOrderStatuses as readonly unknown[]).includes(v);
}

export interface PurchaseOrderRow {
  id: string;
  number: string;
  status: PurchaseOrderStatus;
  supplier_id: string;
  supplier_code: string;
  supplier_name: string;
  order_date: string;
  next_promised_date: string | null;
  total_amount: string | number;
  currency: string;
  line_count: number;
}

export interface PurchaseOrderLine {
  id: string;
  line_no: number;
  item_id: string;
  item_code: string;
  item_description: string;
  lot_controlled: boolean;
  /** Absent in demo data and older payloads. */
  serial_controlled?: boolean;
  ordered_qty: string | number;
  uom_id: string;
  uom_code: string;
  uom_decimals: number;
  ordered_base_qty: string | number;
  received_base_qty: string | number;
  base_uom_code: string;
  base_uom_decimals: number;
  unit_price: string | number | null;
  line_amount: string | number | null;
  promised_date: string | null;
  warehouse_id: string;
  warehouse_code: string;
  location_id: string | null;
  location_code: string | null;
  tolerance_pct: string | number;
  status: "open" | "received" | "closed";
  /** Subcontract line: the production order operation it buys. */
  subcontract?: {
    operation_id: string;
    operation_seq: number;
    operation_description: string;
    order_id: string;
    order_number: string;
    order_status: string;
    work_center_code: string;
  } | null;
  /** Item subcontracting (ADR-055): the subcontractor warehouse and the materials it consumes. */
  subcontract_warehouse_id?: string | null;
  subcontract_warehouse_code?: string | null;
  materials?: {
    id: string;
    seq: number;
    item_id: string;
    item_code: string;
    item_description: string;
    required_base_qty: string | number;
    consumed_base_qty: string | number;
    /** Gross: posted transfers linked to the line into the subcontractor warehouse. */
    sent_base_qty: string | number;
    base_uom_code: string;
    base_uom_decimals: number;
    need_date: string;
  }[] | null;
}

export interface PurchaseOrderDocument {
  id: string;
  number: string;
  status: PurchaseOrderStatus;
  company_id: string;
  company_code: string;
  supplier_id: string;
  supplier_code: string;
  supplier_name: string;
  order_date: string;
  currency: string;
  supplier_reference: string | null;
  note: string | null;
  warehouse_id: string | null;
  warehouse_code: string | null;
  next_promised_date: string | null;
  total_amount: string | number;
  revision: number;
  line_count: number;
  released_at: string | null;
  closed_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
  created_by_name: string | null;
  lines: PurchaseOrderLine[];
}

export interface PurchaseOrderHeaderInput {
  order_date: string;
  currency: string;
  warehouse_id: string | null;
  supplier_reference: string | null;
  note: string | null;
  expected_revision?: number;
}

export interface PurchaseOrderLineInput {
  line_id?: string;
  item_id: string;
  qty: string;
  /** Leave out on a new line to take the purchase unit (ADR-050). */
  uom_id?: string;
  /** Null on a new line: the agreed / last / standard price. */
  unit_price: string | null;
  /** Null on a new line: order date + the supplier item's lead time. */
  promised_date: string | null;
  warehouse_id?: string | null;
  location_id?: string | null;
  tolerance_pct?: string;
}

export interface SaveResult {
  id: string;
  number: string;
  status: PurchaseOrderStatus;
  revision: number;
}

export interface ListParams {
  companyId: string;
  status: StatusFilter;
  q: string;
  /** Column filters and sort (purchase_order_page spec, purchaseOrderListColumns). */
  filters?: ListFilter[];
  sort?: ListSort | null;
}

export const PO_PAGE = 50;

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

export async function listPurchaseOrders(p: ListParams, cursor: string | null, signal: AbortSignal, size = PO_PAGE): Promise<Page<PurchaseOrderRow>> {
  if (isDemoMode) return demoPoApi.list(p, cursor);
  const { data, error } = await db()
    .rpc("purchase_order_page", {
      p_company_id: p.companyId,
      p_status: p.status,
      p_term: p.q.trim() || null,
      p_filters: p.filters ?? [],
      p_sort: sortParam(p.sort ?? null),
      p_after: cursor ? JSON.parse(cursor) : null,
      p_limit: size + 1,
    })
    .abortSignal(signal);
  if (error) throw error;
  const rows = data as (PurchaseOrderRow & { sort_cursor: unknown })[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  const last = page[page.length - 1];
  return { rows: page, nextCursor: hasMore && last ? JSON.stringify(last.sort_cursor) : null };
}

async function getPurchaseOrder(id: string, signal: AbortSignal): Promise<PurchaseOrderDocument | null> {
  if (isDemoMode) return demoPoApi.get(id);
  const { data, error } = await db().rpc("purchase_order_document", { p_id: id }).abortSignal(signal);
  if (error) throw error;
  return (data as PurchaseOrderDocument | null) ?? null;
}

export async function savePurchaseOrder(
  id: string | null,
  companyId: string,
  supplierId: string,
  header: PurchaseOrderHeaderInput,
  lines: PurchaseOrderLineInput[],
): Promise<SaveResult> {
  if (isDemoMode) return demoPoApi.save(id, companyId, supplierId, header, lines);
  const { data, error } = await db().rpc("save_purchase_order", {
    p_id: id,
    p_company_id: companyId,
    p_supplier_id: supplierId,
    p_header: header,
    p_lines: lines,
  });
  if (error) throw error;
  return data as SaveResult;
}

type Transition = "release" | "cancel" | "close";

export async function transitionPurchaseOrder(id: string, action: Transition): Promise<void> {
  if (isDemoMode) return demoPoApi.transition(id, action);
  const fn = { release: "release_purchase_order", cancel: "cancel_purchase_order", close: "close_purchase_order" }[action];
  const { error } = await db().rpc(fn, { p_id: id });
  if (error) throw error;
}

export async function closePurchaseOrderLine(id: string, lineId: string): Promise<void> {
  if (isDemoMode) return demoPoApi.closeLine(id, lineId);
  const { error } = await db().rpc("close_purchase_order_line", { p_id: id, p_line_id: lineId });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Goods receipts against a PO (post_purchase_receipt / reverse_purchase_receipt)
// ---------------------------------------------------------------------------
//   receipts  rpc purchase_order_receipts(order) — receipts + lines, one round-trip
//             (index purchase_receipts_order_idx); loaded with the PO detail.
//   post      one RPC: validates open qty + tolerance, one engine call, PO status.

export interface ReceiptLineInput {
  order_line_id: string;
  qty: string;
  location_id?: string;
  lot_code?: string;
  supplier_lot_code?: string;
  expiry_date?: string;
  serials?: string[];
}

export interface PurchaseReceipt {
  id: string;
  number: string;
  status: "draft" | "posted" | "cancelled";
  delivery_note: string | null;
  note: string | null;
  reverses_receipt_id: string | null;
  reversed_by_id: string | null;
  posted_at: string;
  posted_by_name: string | null;
  lines: {
    line_no: number;
    order_line_id: string;
    item_code: string;
    qty: string | number;
    uom_code: string;
    base_qty: string | number | null;
    location_code: string | null;
    lot_code: string | null;
    /** The first 10 serials of the line; serial_count tells how many there are. */
    serial_numbers?: string[] | null;
    serial_count?: number | null;
  }[];
}

export interface ReceiptPage {
  receipts: PurchaseReceipt[];
  /** More than the 50 newest receipts exist. */
  has_more: boolean;
}

async function listReceipts(orderId: string, signal: AbortSignal): Promise<ReceiptPage> {
  if (isDemoMode) return { receipts: [], has_more: false };
  const { data, error } = await db().rpc("purchase_order_receipts", { p_order_id: orderId }).abortSignal(signal);
  if (error) throw error;
  return (data as ReceiptPage | null) ?? { receipts: [], has_more: false };
}

export async function postPurchaseReceipt(
  orderId: string,
  deliveryNote: string | null,
  lines: ReceiptLineInput[],
): Promise<{ id: string; number: string; order_status: PurchaseOrderStatus }> {
  if (isDemoMode) throw new Error("Receipts are not available in demo mode");
  const { data, error } = await db().rpc("post_purchase_receipt", {
    p_order_id: orderId,
    p_delivery_note: deliveryNote,
    p_lines: lines,
  });
  if (error) throw error;
  return data as { id: string; number: string; order_status: PurchaseOrderStatus };
}

/** Receive subcontracted work: reports the MO operation (good / scrap), no stock moves. */
export async function postSubcontractReceipt(
  orderLineId: string,
  input: { good: string; scrap: string; deliveryNote: string | null },
): Promise<{ id: string; number: string; order_status: PurchaseOrderStatus }> {
  if (isDemoMode) throw new Error("Receipts are not available in demo mode");
  const { data, error } = await db().rpc("post_subcontract_receipt", {
    p_order_line_id: orderLineId,
    p_good: Number(input.good.replace(",", ".") || 0),
    p_scrap: Number(input.scrap.replace(",", ".") || 0),
    p_delivery_note: input.deliveryNote,
  });
  if (error) throw error;
  return data as { id: string; number: string; order_status: PurchaseOrderStatus };
}

// Item subcontracting (ADR-055): material to the subcontractor for a PO line.
//   proposal  rpc subcontract_line_send_proposal(line) — required − sent per material,
//             FEFO picks from the line's receipt warehouse (one round-trip, read).
//   send      rpc send_to_subcontractor_line(line, lines, note) — one stock transfer.
export interface LineSendProposal {
  order_line_id: string;
  line_no: number;
  item_code: string;
  order_number: string;
  supplier_code: string;
  from_warehouse_code: string | null;
  to_warehouse_code: string | null;
  lines: {
    material_id: string;
    seq: number;
    item_code: string;
    item_description: string;
    serial_controlled?: boolean;
    base_uom_code: string;
    base_uom_decimals: number;
    need_base_qty: string | number;
    picks: SendPick[];
  }[];
}

async function getLineSendProposal(lineId: string, signal: AbortSignal): Promise<LineSendProposal | null> {
  if (isDemoMode) return null;
  const { data, error } = await db().rpc("subcontract_line_send_proposal", { p_line_id: lineId }).abortSignal(signal);
  if (error) throw error;
  return (data as LineSendProposal | null) ?? null;
}

export function lineSendProposalQuery(lineId: string) {
  return queryOptions({
    queryKey: [...purchaseOrderKeys.all, "send-proposal", lineId] as const,
    queryFn: ({ signal }) => getLineSendProposal(lineId, signal),
    staleTime: 0,
  });
}

export async function sendToSubcontractorLine(lineId: string, lines: SendMaterialInput[], note: string | null) {
  if (isDemoMode) throw new Error("Not available in demo mode");
  // Zod loads with the form, not with the route config (initial bundle).
  const { sendToSubcontractorLineInput } = await import("@patronix/domain/subcontracting");
  const input = sendToSubcontractorLineInput.parse({ p_line_id: lineId, p_lines: lines, p_note: note });
  const { data, error } = await db().rpc("send_to_subcontractor_line", input);
  if (error) throw error;
  return data as { id: string; number: string };
}

export async function reversePurchaseReceipt(id: string): Promise<void> {
  if (isDemoMode) throw new Error("Receipts are not available in demo mode");
  const { error } = await db().rpc("reverse_purchase_receipt", { p_id: id });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Query options (loaders, hover prefetch and components share them)
// ---------------------------------------------------------------------------

export const purchaseOrderKeys = {
  all: ["procurement", "purchase-orders"] as const,
  list: (p: ListParams) => [...purchaseOrderKeys.all, "list", p] as const,
  detail: (id: string) => [...purchaseOrderKeys.all, "detail", id] as const,
  receipts: (id: string) => [...purchaseOrderKeys.all, "receipts", id] as const,
};

export function purchaseOrderReceiptsQuery(id: string) {
  return queryOptions({
    queryKey: purchaseOrderKeys.receipts(id),
    queryFn: ({ signal }) => listReceipts(id, signal),
    staleTime: 30_000,
  });
}

export function purchaseOrderListQuery(params: ListParams) {
  return infiniteQueryOptions({
    queryKey: purchaseOrderKeys.list(params),
    queryFn: ({ pageParam, signal }) => listPurchaseOrders(params, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 30_000,
    enabled: !!params.companyId,
  });
}

export function purchaseOrderDetailQuery(id: string) {
  return queryOptions({
    queryKey: purchaseOrderKeys.detail(id),
    queryFn: ({ signal }) => getPurchaseOrder(id, signal),
    staleTime: 30_000,
  });
}

export const statusTone = {
  draft: "neutral",
  released: "info",
  partially_received: "warning",
  received: "success",
  closed: "neutral",
  cancelled: "danger",
} as const satisfies Record<PurchaseOrderStatus, string>;

export const isOpenStatus = (s: PurchaseOrderStatus) => s === "draft" || s === "released" || s === "partially_received";

/** Translated message for known database rules; anything else as sent. */
const rules: [RegExp, string][] = [
  [/was changed by someone else/, "po.err.stale"],
  [/^Supplier .* is inactive/, "po.err.supplierInactive"],
  [/^Supplier not found/, "po.err.supplierNotFound"],
  [/^Supplier and company cannot change after release/, "po.err.supplierLocked"],
  [/^Currency cannot change after release/, "po.err.currencyLocked"],
  [/^Currency must be a three-letter code/, "po.err.currency"],
  [/^Warehouse not found in the company or inactive/, "po.err.warehouse"],
  [/^The supplier is inactive/, "po.err.supplierInactive"],
  [/^A purchase order needs at least one line/, "po.err.noLines"],
  [/^Goods were received on this order/, "po.err.receivedCannotCancel"],
  [/^Only draft or released purchase orders without receipts can be cancelled/, "po.err.cannotCancel"],
  [/^Only draft purchase orders can be released/, "po.err.cannotRelease"],
  [/^Missing permission procurement.receipt/, "po.err.receiptPermission"],
  [/^Missing permission/, "po.err.permission"],
  [/^Goods can be received only on released orders/, "po.err.notReceivable"],
  [/^A receipt needs at least one line/, "po.err.receiptEmpty"],
  [/receipts on it can no longer be reversed/, "po.err.reverseClosed"],
  [/is already reversed/, "po.err.alreadyReversed"],
  [/^Not enough stock/, "po.err.reverseMoved"],
  [/at the subcontractor/, "po.err.subcontractShort"],
  [/serial-controlled and cannot be consumed by a receipt at the subcontractor/, "po.err.subcontractSerial"],
  [/^Material is sent only for open lines of released orders/, "po.err.sendNotOpen"],
  [/must be active and in the site of the line's receipt warehouse|is the subcontractor warehouse; nothing to send/, "sub.err.noWarehouse"],
];

const lineRules: [RegExp, string][] = [
  [/^price is missing/, "po.err.priceMissing"],
  [/^lot is required/, "documents.err.lotRequired"],
  [/^not a material of this line/, "sub.err.notLine"],
  [/more than ordered plus the/, "po.err.overTolerance"],
  [/^the order line is closed/, "po.err.orderLineClosed"],
  [/^lot code:/, "po.err.lotCode"],
  [/^location does not receive/, "documents.err.locationNoReceive"],
  [/^location is blocked/, "documents.err.locationBlocked"],
  [/^promised date is missing/, "po.err.dateMissing"],
  [/^item is inactive/, "po.err.itemInactive"],
  [/^item and quantity are required/, "po.err.incompleteLine"],
  [/^quantity must be above zero/, "po.err.qtyPositive"],
  [/^services cannot be ordered yet/, "po.err.service"],
  [/^serial numbers are required/, "serials.err.required"],
  [/^\d+ serial numbers for a quantity of/, "serials.err.count"],
  [/cannot be below what was already received/, "po.err.belowReceived"],
  [/cannot change once goods were received/, "po.err.receivedLocked"],
  [/^line is closed and cannot change/, "po.err.lineClosed"],
  [/cannot be expressed in the base unit/, "documents.err.decimals"],
  [/^No unit conversion/, "documents.err.noConversion"],
];

export function poErrorMessage(t: TFunction, error: unknown): string {
  const tr = t as unknown as (key: string, o?: Record<string, string>) => string;
  const message = (error as { message?: string } | null)?.message ?? String(error ?? "");
  const line = /^Line (\d+): (.+)$/.exec(message);
  if (line) {
    const rule = lineRules.find(([re]) => re.test(line[2] ?? ""));
    return tr("po.err.line", { line: line[1] ?? "", message: rule ? tr(rule[1]) : (line[2] ?? "") });
  }
  const rule = rules.find(([re]) => re.test(message));
  return rule ? tr(rule[1]) : message || tr("auth.errorGeneric");
}
